import React, { useMemo, type ComponentType } from 'react';
import { useGetContextTokenForType } from '@atlassian/jira-forge-controllers-context-token/src/index.tsx';
import { useMemoizedStateObject } from '@atlassian/jira-forge-ui-controllers/src/controllers/memoized-state-object/index.tsx';
import type {
	GetContextToken,
	ContextTokenType,
} from '@atlassian/jira-forge-ui-types/src/common/types/context-token.tsx';
import type { ExtensionData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type { ExtensionPayload } from '@atlassian/jira-forge-ui-types/src/common/types/extension-payload.tsx';
import type { Extension } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';

type ProvidedProps = {
	extension: Extension;
	extensionData: ExtensionData;
	extensionPayload?: ExtensionPayload;
	getContextToken?: GetContextToken;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withCommonRenderer = <TProps extends Record<string, any>>(
	RendererImplementation: ComponentType<TProps>,
) => {
	const RendererWithProps = (
		props: Omit<TProps, 'getContextToken'> & { contextToken?: ContextTokenType },
	) => {
		const { contextToken: contextTokenType, localId, ...restProps } = props;
		const extensionDataWithExtras = useMemo(
			() => ({
				...props.extensionData,
				jira: {
					...props.extensionData.jira,
					isNewNavigation: true,
				},
			}),
			[props.extensionData],
		);
		const extension = useMemoizedStateObject(props.extension);
		const extensionData = useMemoizedStateObject(extensionDataWithExtras);
		const extensionPayload = useMemoizedStateObject(props.extensionPayload);
		const getContextToken = useGetContextTokenForType(
			contextTokenType,
			extension,
			extensionData,
			localId,
		);
		const extraProps: ProvidedProps = useMemo(
			() => ({
				extension,
				extensionData,
				extensionPayload,
				localId,
				getContextToken,
			}),
			[extension, extensionData, extensionPayload, localId, getContextToken],
		);

		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return <RendererImplementation {...(restProps as TProps)} {...extraProps} />;
	};

	const { name, displayName } = RendererImplementation;
	RendererWithProps.displayName = `withCommonRenderer(${name || displayName || 'UNNAMED'})`;

	return RendererWithProps;
};
